import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Loader, Sparkles, Upload, X, MapPin, UserCircle, ExternalLink } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import ShareIdeaRow from '../components/ShareIdeaRow';
import SaveAnswerPrompt from '../components/SaveAnswerPrompt';
import ExpertNoteByline from '../components/ExpertNoteByline';
import ExpertNoteFollowUpCta from '../components/ExpertNoteFollowUpCta';
import AiIdeasAnalysisSections from '../components/AiIdeasAnalysisSections';
import { getIdeas } from '../services/landscapingQuestionsApi';
import { convertHeicToJpeg, isHeicFile } from '../utils/convertHeic';
import {
  getCustomerUserId,
  isLoggedInCustomer,
  loadYardProfile,
  saveQuestionToAccount,
  buildYardContextPromptPrefix,
  goalLabelById,
} from '../utils/customerAccount';

function usePublicUserId() {
  try {
    const raw = localStorage.getItem('userData') || localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw)?.id || null;
  } catch {
    return null;
  }
}

const YARD_BRIEF_PREFILL_STORAGE = 'yardBriefPrefill';

function readLoggedInEmail() {
  try {
    const raw = localStorage.getItem('userData') || localStorage.getItem('user');
    if (!raw) return '';
    const u = JSON.parse(raw);
    return (u.email || u.userEmail || '').trim();
  } catch {
    return '';
  }
}

const THEMES = [
  { id: 'privacy', title: 'Privacy Planting', preset: 'I want more privacy in my yard. What planting layers, spacing, and plant types should I consider for screening?' },
  { id: 'low-maintenance', title: 'Low Maintenance', preset: 'I want a lower-maintenance yard. What plant choices and bed layouts reduce ongoing work?' },
  { id: 'shade', title: 'Shade Solutions', preset: 'I have challenging shade in my yard. What plants and design moves work for dry or moist shade?' },
  { id: 'small-yard', title: 'Small Yards', preset: 'I have a small yard. How should I prioritize plants and layout for maximum impact?' },
  { id: 'curb-appeal', title: 'Curb Appeal', preset: 'I want better curb appeal. What front-yard planting ideas make the biggest difference?' },
  { id: 'four-season', title: 'Four-Season Interest', preset: 'I want year-round interest in my yard. How do I layer evergreens, blooms, and fall color?' },
];

function compressImage(file, maxDim = 1920, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width;
        let h = img.height;
        if (w > maxDim || h > maxDim) {
          if (w > h) { h = Math.round((h * maxDim) / w); w = maxDim; }
          else { w = Math.round((w * maxDim) / h); h = maxDim; }
        }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function YardIdeasPage() {
  const currentUserId = usePublicUserId();
  const location = useLocation();
  const navigate = useNavigate();
  const [yardProfile, setYardProfile] = useState(null);

  // Form state
  const [question, setQuestion] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [askerEmail, setAskerEmail] = useState('');
  const [notifyExpertNote, setNotifyExpertNote] = useState(false);
  const [shareCommunity, setShareCommunity] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Result state
  const [result, setResult] = useState(null);

  const fileInputRef = useRef(null);
  const resultRef = useRef(null);

  useEffect(() => {
    document.title = 'Get Ideas | PlantaDay';
  }, []);

  useEffect(() => {
    const uid = getCustomerUserId();
    setYardProfile(uid ? loadYardProfile(uid) : null);
  }, [currentUserId, location.key]);

  // Scroll to hash anchor (goal chips on home page link to #privacy etc.)
  useEffect(() => {
    const hash = location.hash?.replace(/^#/, '');
    if (!hash || hash === 'form') return;
    const t = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.hash, location.key]);

  // Pre-fill question from router state (goal chips or other pages)
  useEffect(() => {
    const preset = location.state?.askPreset;
    if (preset && typeof preset === 'string') {
      setQuestion(preset);
    }
  }, [location.key, location.state?.askPreset]);

  const handleImageSelect = async (e) => {
    let file = e.target.files[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setError('Photo must be under 15 MB.');
      return;
    }
    setError(null);
    if (isHeicFile(file)) {
      try {
        file = await convertHeicToJpeg(file);
      } catch (err) {
        setError(err.message || 'Could not convert HEIC image. Please try a JPG or PNG.');
        return;
      }
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      let image_base64 = null;
      if (imageFile) {
        image_base64 = await compressImage(imageFile);
      }

      const user_location = (city.trim() || stateProvince.trim())
        ? { city: city.trim() || null, state: stateProvince.trim() || null }
        : null;

      const profile = loadYardProfile(getCustomerUserId());
      const prefix = buildYardContextPromptPrefix(profile);
      const questionForApi = prefix ? `${prefix}${q}` : q;

      const data = await getIdeas({
        question: questionForApi,
        image_base64,
        user_location,
        asker_email: askerEmail,
        notify_expert_note: notifyExpertNote,
        is_shared: shareCommunity,
      });
      setResult(data);

      const cid = getCustomerUserId();
      if (cid && data?.id) {
        saveQuestionToAccount(cid, {
          id: data.id,
          question_text: q,
          ai_answer: data.ai_answer || '',
          created_at: data.created_at || new Date().toISOString(),
          expert_notes: data.expert_notes || [],
        });
      }

      // Scroll to results
      window.setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendYardBrief = () => {
    if (!result?.id) return;
    const payload = {
      photoDataUrl: imagePreview || null,
      questionText: result.question_text || '',
      email: (askerEmail || '').trim() || readLoggedInEmail(),
      city: (city || '').trim() || '',
      questionId: result.id,
    };
    const firstDesigner = result.matchedDesigners?.[0];
    if (firstDesigner?.id) {
      navigate(
        `/send-brief/${firstDesigner.id}?questionId=${encodeURIComponent(result.id)}`,
        { state: { yardBriefPrefill: payload } }
      );
    } else {
      try {
        sessionStorage.setItem(YARD_BRIEF_PREFILL_STORAGE, JSON.stringify(payload));
      } catch {
        /* ignore quota */
      }
      navigate('/find-designer');
    }
  };

  const handleNewQuestion = () => {
    setResult(null);
    setQuestion('');
    clearImage();
    setCity('');
    setStateProvince('');
    setAskerEmail('');
    setNotifyExpertNote(false);
    setShareCommunity(false);
    setError(null);
    window.setTimeout(() => {
      document.getElementById('ideas-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNav currentUserId={currentUserId} />
      <main className="flex-1 max-w-4xl mx-auto px-4 py-10 sm:py-14 w-full">

        {isLoggedInCustomer() && yardProfile && (yardProfile.cityRegion || yardProfile.goalId) && (
          <div className="mb-6 rounded-xl border border-green-200 bg-green-50/90 px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-sm text-green-900">
            <p>
              <span className="font-medium">Answering for:</span>{' '}
              {yardProfile.cityRegion ? <span>{yardProfile.cityRegion}</span> : null}
              {yardProfile.cityRegion && yardProfile.goalId ? ', ' : ''}
              {yardProfile.goalId ? (
                <span>
                  goal: {goalLabelById(yardProfile.goalId)}
                </span>
              ) : null}
            </p>
            <Link to="/yard-profile" className="font-semibold text-green-800 hover:underline shrink-0">
              Edit
            </Link>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Get ideas</h1>
          <p className="mt-2 text-gray-600 max-w-2xl leading-relaxed">
            Describe your yard or ask a question. Add a photo for more specific feedback. AI answers first.
            When a local designer reviews your question, you will see their note here too.
          </p>
        </div>

        {/* Goal tiles — shown above the form so users know what they can ask before typing or uploading */}
        {!result && (
          <section id="guided-lookup" className="mb-8 scroll-mt-24">
            <h2 className="text-base font-semibold text-gray-900 mb-1">What is your yard goal?</h2>
            <p className="text-sm text-gray-500 mb-4">Pick one to fill in a starting question, or write your own below.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {THEMES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setQuestion(t.preset);
                    document.getElementById('ideas-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="text-left rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm hover:border-green-400 hover:bg-green-50/40 transition-all"
                >
                  <span className="block font-semibold text-sm text-gray-900">{t.title}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Form */}
        {!result && (
          <section id="ideas-form" className="scroll-mt-24">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8 space-y-5">

              {/* Question */}
              <div>
                <label htmlFor="yard-question" className="block text-sm font-medium text-gray-700 mb-1">
                  Describe your yard or ask a question
                </label>
                <textarea
                  id="yard-question"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows={4}
                  maxLength={3000}
                  disabled={loading}
                  placeholder="e.g. I want more privacy along my back fence. We get afternoon sun and the soil is clay."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 resize-none"
                />
                <p className="mt-1 text-xs text-gray-400">{question.length} / 3000</p>
              </div>

              {/* Photo upload */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Photo <span className="text-gray-400 font-normal">(optional - adds a lot)</span>
                </p>
                {imagePreview ? (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Your yard"
                      className="h-48 rounded-xl object-cover border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={clearImage}
                      className="absolute -top-2 -right-2 w-7 h-7 bg-white border border-gray-300 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50"
                      aria-label="Remove photo"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-green-400 hover:bg-green-50/30 transition-colors w-full sm:w-auto inline-flex">
                    <Upload className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600">Upload a yard photo</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,image/heic,image/heif,.heic,.heif"
                      className="sr-only"
                      onChange={handleImageSelect}
                      disabled={loading}
                    />
                  </label>
                )}
                {imagePreview && (
                  <p className="mt-2 text-xs text-green-700 font-medium">Photo added - AI will analyze your specific space.</p>
                )}
              </div>

              {/* Location */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  Your location{' '}
                  <span className="text-gray-400 font-normal">(helps AI with your climate zone + connects local designers)</span>
                </p>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    disabled={loading}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    value={stateProvince}
                    onChange={(e) => setStateProvince(e.target.value)}
                    placeholder="State / Province"
                    disabled={loading}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">{error}</div>
              )}

              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyExpertNote}
                  onChange={(e) => setNotifyExpertNote(e.target.checked)}
                  disabled={loading}
                  className="mt-0.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span>Email me when a designer adds a note to my question (optional)</span>
              </label>
              {notifyExpertNote && (
                <div>
                  <label htmlFor="yard-notify-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email for notifications
                  </label>
                  <input
                    id="yard-notify-email"
                    type="email"
                    autoComplete="email"
                    value={askerEmail}
                    onChange={(e) => setAskerEmail(e.target.value)}
                    disabled={loading}
                    placeholder="you@example.com"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    We only use this address to email you when a designer publishes a note on this question — no account
                    required.
                  </p>
                  {!isLoggedInCustomer() && (
                    <p className="mt-2 text-xs text-gray-600 leading-snug">
                      <span className="font-medium text-gray-800">Optional:</span> After you get ideas, create a free
                      account (or log in) to save this question in <span className="font-medium">My questions</span> and
                      keep everything in one place alongside the same notifications.
                    </p>
                  )}
                </div>
              )}

              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shareCommunity}
                  onChange={(e) => setShareCommunity(e.target.checked)}
                  disabled={loading}
                  className="mt-0.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span>
                  Include this question and AI ideas in the{' '}
                  <Link to="/community" className="text-green-700 font-medium hover:underline">
                    Community
                  </Link>{' '}
                  feed (you stay anonymous — avoid personal details in your question)
                </span>
              </label>

              <button
                type="submit"
                disabled={loading || !question.trim()}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <><Loader className="w-5 h-5 animate-spin" /> Getting ideas{imageFile ? ' from your photo' : ''}...</>
                ) : (
                  <><Sparkles className="w-5 h-5" /> Get ideas</>
                )}
              </button>
              <p className="text-xs text-gray-400">
                AI ideas are a starting point. For site-specific or structural work, consult a local professional.
              </p>
            </form>
          </section>
        )}

        {/* Results */}
        {result && (
          <section ref={resultRef} className="scroll-mt-24 space-y-6">

            {result?.is_shared && result?.id && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/90 p-4 space-y-2">
                <p className="text-sm font-semibold text-emerald-900">Community</p>
                <p className="text-sm text-emerald-800">
                  This question is in the Community feed (anonymous).{' '}
                  <Link to="/community" className="font-medium underline hover:text-emerald-950">
                    Browse Community
                  </Link>{' '}
                  or use your share link below to find this post anytime.
                </p>
              </div>
            )}

            {result.id && (notifyExpertNote && askerEmail.trim()) && (
              <div className="rounded-xl border border-green-200 bg-green-50/80 p-4 space-y-2">
                <p className="text-sm font-semibold text-green-900">Email updates</p>
                <p className="text-sm text-green-800">
                  We will email you at <span className="font-medium">{askerEmail.trim()}</span> when an expert publishes a
                  note. You do not need an account for that — optional sign-in below only helps you save answers in{' '}
                  <span className="font-medium">My questions</span>.
                </p>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Your question</p>
              <p className="text-gray-900">{result.question_text}</p>
              {result.has_photo && (
                <span className="inline-block mt-2 text-xs px-2.5 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">Photo analyzed</span>
              )}
            </div>

            {/* AI summary / answer */}
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/80 p-5 sm:p-6">
              <div className="flex items-center gap-2 text-emerald-900 mb-3">
                <Sparkles className="w-5 h-5 flex-shrink-0" />
                <h2 className="text-lg font-bold">AI ideas</h2>
              </div>
              <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{result.ai_answer}</p>
              <p className="mt-4 text-xs text-emerald-800/70">
                Generated by AI. Verify important decisions with local pros and your own site conditions.
              </p>
              {result.id && <ShareIdeaRow questionId={result.id} />}
              {!isLoggedInCustomer() && <SaveAnswerPrompt />}
            </div>

            <AiIdeasAnalysisSections analysis={result.analysis} matchedPlants={result.matchedPlants} />

            {/* Expert notes (if any designer has reviewed) */}
            {result.expert_notes?.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Designer notes</h2>
                {result.expert_notes.map((note) => (
                  <div key={note.id} className="rounded-xl border-2 border-blue-200 bg-blue-50/90 p-5">
                    <div className="flex items-center gap-2 text-blue-900 mb-2 flex-wrap">
                      <UserCircle className="w-5 h-5 flex-shrink-0" />
                      <span className="font-bold">Designer note</span>
                      {note.contributor_label && (
                        <span className="text-sm text-blue-700">
                          (
                          <ExpertNoteByline
                            note={note}
                            className="text-sm text-blue-700"
                            inline
                          />
                          )
                        </span>
                      )}
                    </div>
                    <p className="text-gray-900 whitespace-pre-wrap leading-relaxed text-sm">{note.advice_text}</p>
                    <ExpertNoteFollowUpCta note={note} className="text-xs text-blue-900/80 mt-3 leading-snug" />
                  </div>
                ))}
              </div>
            )}

            <div className="pt-2">
              <div className="border-t border-gray-200" aria-hidden />
              <div className="pt-8">
                <h2 className="text-lg font-semibold text-gray-900">Want to take this further?</h2>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                  Send your photo and question to a designer. They will pick it up in their project tools and work with you
                  from there.
                </p>
                <button
                  type="button"
                  onClick={handleSendYardBrief}
                  className="mt-4 inline-flex items-center justify-center px-6 py-3 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors shadow-sm"
                >
                  Send a yard brief
                </button>
                <p className="mt-2 text-xs text-gray-400">Free to send. No obligation.</p>
              </div>
            </div>

            {/* Matched designers */}
            {result.matchedDesigners?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-1">Designers in your area</h3>
                <p className="text-sm text-gray-500 mb-4">When you reach out, share this page link so they can see your question and the AI analysis.</p>
                <ul className="space-y-4">
                  {result.matchedDesigners.map((d) => (
                    <li key={d.id} className="flex items-start gap-3">
                      {d.profilePhoto ? (
                        <img src={d.profilePhoto} alt={d.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 text-green-700 font-semibold text-sm">
                          {(d.name || d.businessName || 'D')[0]}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 text-sm">{d.businessName || d.name}</p>
                        {(d.city || d.provinceState) && (
                          <p className="text-xs text-gray-500">{[d.city, d.provinceState].filter(Boolean).join(', ')}{d.distance_km != null ? ` - ${d.distance_km} km away` : ''}</p>
                        )}
                        {d.email && (
                          <a
                            href={`mailto:${d.email}?subject=Yard%20design%20question%20from%20PlantADay&body=Hi%2C%20I%20had%20a%20question%20about%20my%20yard.%20Here%20is%20my%20PlantADay%20result%3A%20${encodeURIComponent(window.location.href)}`}
                            className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline"
                          >
                            Contact <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="button"
                onClick={handleNewQuestion}
                className="px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors"
              >
                Ask another question
              </button>
              <Link to="/find-designer" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:underline">
                Browse all designers <ExternalLink className="w-4 h-4" />
              </Link>
            </div>

            <p className="text-xs text-gray-400">
              Bookmark this page or use the copy link above. If you asked for email updates, we will notify you when a note is published.
            </p>
          </section>
        )}


      </main>
      <Footer />
    </div>
  );
}
